import { useEffect, useState } from 'react';
import { m as motion, LazyMotion, domAnimation, AnimatePresence } from 'framer-motion';
import { FiGithub, FiLinkedin, FiMail, FiMenu, FiX, FiChevronRight, FiCheckCircle } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import heroImage from './assets/hero_image.webp';

// Translations Dictionary for EN and PT
const text = {
  en: {
    nav: { home: 'Home', about: 'About', services: 'Services', projects: 'Projects', contact: 'Contact' },
    hero: {
      headline: "AI-Powered Software Solutions for Your Business",
      subhead: "I help businesses save time, reduce errors, and grow revenue through custom software development and seamless AI integration.",
      cta: "Book a Free Consultation",
      ctaSecondary: "View Case Studies"
    },
    about: {
      title: "About Me",
      story: "Hello! I'm Jatan Kumar, a software developer based in Lisbon, Portugal. My focus is on understanding your unique business challenges and building solutions that deliver real, measurable value. I don't just write code; I partner with business owners to optimize processes, integrate modern AI tools, and create software that scales with your growth.",
      techText: "I specialize in modern computing architectures, web technologies, and AI system integrations."
    },
    services: {
      title: "How I Help Businesses Thrive",
      items: [
        {
          title: "Custom Business Software",
          desc: "Tailored web platforms and internal tools designed to manage your operations securely and efficiently. Say goodbye to spreadsheets."
        },
        {
          title: "AI Integration & Automation",
          desc: "Automate repetitive workflows and enhance your team's decision-making by securely integrating the latest Artificial Intelligence models."
        },
        {
          title: "System Integration & APIs",
          desc: "Connect your disparate software tools so your digital ecosystem works seamlessly together, eliminating manual data entry."
        },
        {
          title: "Technical Consulting",
          desc: "Get expert advice on your technical strategy. I help you map out prototypes and validate new software ideas before heavy investment."
        }
      ]
    },
    projects: {
      title: "Featured Case Studies",
      items: [
        {
          title: "Diversity Blends",
          problem: "The client needed a scalable online platform to showcase and sell diverse culinary products without managing complex legacy systems.",
          solution: "Built a modern e-commerce solution using React and Node.js with secure payment gateways and scalable Docker containerization.",
          outcome: "Resulted in a faster, more reliable shopping experience, directly contributing to increased online sales and customer retention.",
          image: "diversity-blends-screenshot.png",
          link: "https://diversityblends.co.za/",
          tech: ["React", "Node.js", "Docker"]
        },
        {
          title: "Design Central",
          problem: "A creative agency lacked a high-performance, visually striking digital portfolio to attract high-ticket enterprise clients.",
          solution: "Developed a lightning-fast, highly responsive portfolio utilizing React, Tailwind CSS, and TypeScript for robust maintainability.",
          outcome: "Improved site load times by 40% and provided a buttery-smooth user experience that increased incoming lead conversions.",
          image: "design-central-screenshot.png",
          link: "https://designcentral.co.za/",
          tech: ["React", "Tailwind CSS", "TypeScript"]
        },
        {
          title: "Javelin Sports",
          problem: "A sports retailer struggled with inventory management and an outdated user interface that was difficult to navigate on mobile.",
          solution: "Engineered a dynamic, mobile-first e-commerce system leveraging PHP, Laravel, and customized database architecture.",
          outcome: "Streamlined inventory tracking and boosted mobile purchasing by delivering a seamless mobile checkout process.",
          image: "javelin-sports-screenshot.png",
          link: "https://javelin-sports.co.za/",
          tech: ["Laravel", "PHP", "Django"]
        }
      ]
    },
    testimonials: {
      title: "Trusted by Businesses",
      subtitle: "See what my clients have to say about working with me.",
      placeholder: "This section will feature testimonials from past clients discussing how our solutions saved them time, reduced errors, and improved their bottom line. (Placeholder - To be added)"
    },
    contact: {
      title: "Let's Optimize Your Business",
      subtitle: "Ready to explore how custom software and AI can transform your operations? Fill out the form or reach out directly.",
      form: {
        name: "Full Name",
        email: "Work Email",
        company: "Company Name",
        message: "Tell me about your current business challenges...",
        submit: "Request Free Consultation"
      },
      nextSteps: "After you submit this form, I will contact you within 24 hours to schedule a brief discovery call."
    },
    footer: "Designed & Built for Business Growth by Jatan Kumar."
  },
  pt: {
    nav: { home: 'Início', about: 'Sobre', services: 'Serviços', projects: 'Projetos', contact: 'Contato' },
    hero: {
      headline: "Soluções de Software com IA para o seu Negócio",
      subhead: "Ajudo empresas a economizar tempo, reduzir erros e aumentar receitas através do desenvolvimento de software sob medida e integração intuitiva de IA.",
      cta: "Agendar Consultoria Gratuita",
      ctaSecondary: "Ver Casos de Sucesso"
    },
    about: {
      title: "Sobre Mim",
      story: "Olá! Sou o Jatan Kumar, um desenvolvedor de software baseado em Lisboa, Portugal. O meu foco é compreender os desafios únicos do seu negócio e construir soluções que entreguem valor real e mensurável. Não apenas escrevo código; trabalho em parceria com empresários para otimizar processos, integrar ferramentas modernas de IA e criar software que cresce com a sua operação.",
      techText: "Especializo-me em arquiteturas modernas, tecnologias web e integrações de sistemas de inteligência artificial."
    },
    services: {
      title: "Como Ajudo Empresas a Prosperar",
      items: [
        {
          title: "Software Empresarial Personalizado",
          desc: "Plataformas web sob medida concebidas para gerir as suas operações de forma segura e eficiente. Diga adeus às dezenas de folhas de cálculo."
        },
        {
          title: "Integração e Automação de IA",
          desc: "Automatize fluxos de trabalho repetitivos e melhore a tomada de decisões da sua equipa integrando com segurança modelos modernos de IA."
        },
        {
          title: "Integração de Sistemas e APIs",
          desc: "Ligue as suas várias ferramentas de software para que o seu ecossistema digital funcione em harmonia, eliminando a entrada manual de dados."
        },
        {
          title: "Consultoria Técnica",
          desc: "Obtenha orientação especializada sobre estratégia tecnológica. Ajudo a planear protótipos e validar novas ideias de software antes de investir fortemente."
        }
      ]
    },
    projects: {
      title: "Casos de Estudo em Destaque",
      items: [
        {
          title: "Diversity Blends",
          problem: "O cliente precisava de uma plataforma escalável para promover e vender produtos culinários diversificados sem gerir sistemas legados complexos.",
          solution: "Construção de uma solução de e-commerce moderna usando React e Node.js com gateways de pagamento seguros e conteinerização Docker escalável.",
          outcome: "Resultou numa experiência de compra mais rápida e fiável, contribuindo diretamente para o aumento das vendas e retenção de clientes.",
          image: "diversity-blends-screenshot.png",
          link: "https://diversityblends.co.za/",
          tech: ["React", "Node.js", "Docker"]
        },
        {
          title: "Design Central",
          problem: "Uma agência criativa carecia de um portfólio digital moderno e rápido para atrair clientes empresariais de alto valor.",
          solution: "Desenvolvimento de um portfólio extremamente responsivo utilizando React, Tailwind CSS e TypeScript para manutenção robusta a longo prazo.",
          outcome: "Melhorou os tempos de carregamento do site em 40% e proporcionou uma experiência de utilizador fluida, aumentando a conversão de leads.",
          image: "design-central-screenshot.png",
          link: "https://designcentral.co.za/",
          tech: ["React", "Tailwind CSS", "TypeScript"]
        },
        {
          title: "Javelin Sports",
          problem: "Um retalhista de desporto tinha dificuldades com a gestão de inventário e uma interface desatualizada, difícil de navegar em dispositivos móveis.",
          solution: "Desenvolvimento de um sistema e-commerce dinâmico focado no mobile utilizando PHP, Laravel e uma arquitetura de base de dados customizada.",
          outcome: "Otimizou o controlo de inventário e impulsionou as compras via mobile ao entregar um processo de checkout direto e otimizado.",
          image: "javelin-sports-screenshot.png",
          link: "https://javelin-sports.co.za/",
          tech: ["Laravel", "PHP", "Django"]
        }
      ]
    },
    testimonials: {
      title: "Aprovado por Empresas",
      subtitle: "Veja o que os meus clientes dizem sobre trabalhar comigo.",
      placeholder: "Esta secção irá apresentar testemunhos de clientes anteriores, abordando como as nossas soluções pouparam tempo, reduziram erros e melhoraram os seus lucros. (Em breve)"
    },
    contact: {
      title: "Vamos Otimizar o seu Negócio",
      subtitle: "Pronto para explorar como o software personalizado e a IA podem transformar a sua operação? Preencha o formulário ou contacte-me.",
      form: {
        name: "Nome Completo",
        email: "Email Profissional",
        company: "Nome da Empresa",
        message: "Fale-me um pouco sobre os desafios atuais do seu negócio...",
        submit: "Solicitar Consultoria Gratuita"
      },
      nextSteps: "Após preencher este formulário, entrarei em contacto num prazo de 24 horas para agendarmos uma breve chamada de descoberta."
    },
    footer: "Desenhado e Desenvolvido para Crescimento Empresarial por Jatan Kumar."
  }
};

type Language = 'en' | 'pt';

function Home() {
  const [lang, setLang] = useState<Language>('en');

  // Detect and set default language based on user's device/browser
  useEffect(() => {
    const userLanguage = navigator.language || (navigator as any).userLanguage;
    if (userLanguage && userLanguage.toLowerCase().startsWith('pt')) {
      setLang('pt');
    }
  }, []);

  const t = text[lang]; // Current language dictionary

  return (
    <>
      {/* Hero Section */}
      <section id="home" className="min-h-screen flex items-center pt-20">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-4xl"
        >
          <div className="inline-block px-4 py-2 bg-secondary/10 border border-secondary/20 rounded-full text-secondary text-sm font-semibold mb-6">
            Software Developer & Tech Consultant
          </div>
          <h1 className="text-4xl md:text-6xl font-bold text-textPrimary leading-tight mb-6">
            {t.hero.headline}
          </h1>
          <p className="text-xl md:text-2xl text-textSecondary mb-10 max-w-2xl leading-relaxed">
            {t.hero.subhead}
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <a
              href="#contact"
              className="px-8 py-4 bg-secondary text-primary font-bold rounded hover:bg-secondary/90 transition-colors text-center shadow-lg hover:shadow-secondary/20"
            >
              {t.hero.cta}
            </a>
            <a
              href="#projects"
              className="px-8 py-4 border-2 border-textSecondary text-textPrimary font-bold rounded hover:border-secondary hover:text-secondary transition-colors text-center"
            >
              {t.hero.ctaSecondary}
            </a>
          </div>
        </motion.div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 border-t border-textSecondary/20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true, margin: "-100px" }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-textPrimary mb-12 flex items-center">
            {t.about.title}
          </h2>
          <div className="grid md:grid-cols-2 gap-12 items-start">
            <div className="text-textSecondary text-lg leading-relaxed space-y-6">
              <p>{t.about.story}</p>
              <p>{t.about.techText}</p>
            </div>
            <div className="flex justify-center md:justify-end">
              <div className="relative group w-full max-w-sm">
                <div className="absolute inset-0 bg-secondary/20 rounded-lg transform translate-x-4 translate-y-4"></div>
                <img
                  src={heroImage}
                  alt="Jatan Kumar"
                  className="relative z-10 w-full rounded-lg shadow-xl"
                  onError={(e) => {
                    // Fallback if photo.jpeg is missing
                    e.currentTarget.src = 'https://via.placeholder.com/400x500/0a192f/64ffda?text=Jatan+Kumar';
                  }}
                />
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 border-t border-textSecondary/20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true, margin: "-100px" }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-textPrimary mb-3 flex items-center">
            {t.services.title}
          </h2>
          <div className="grid md:grid-cols-2 gap-8 mt-12">
            {t.services.items.map((service, index) => (
              <div key={index} className="bg-primary/50 p-8 rounded-lg border border-textSecondary/20 hover:border-secondary/50 transition-all hover:-translate-y-1">
                <FiCheckCircle className="text-secondary w-8 h-8 mb-4" />
                <h3 className="text-2xl font-bold text-textPrimary mb-4">{service.title}</h3>
                <p className="text-textSecondary leading-relaxed">{service.desc}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="py-24 border-t border-textSecondary/20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true, margin: "-100px" }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-textPrimary mb-12 flex items-center">
            {t.projects.title}
          </h2>
          <div className="space-y-24">
            {t.projects.items.map((project, index) => (
              <div key={index} className={`flex flex-col md:flex-row gap-8 ${index % 2 !== 0 ? 'md:flex-row-reverse' : ''}`}>
                <div className="md:w-1/2 relative group">
                  <a href={project.link} target="_blank" rel="noopener noreferrer" className="block w-full h-full relative overflow-hidden rounded-lg border border-textSecondary/20 group-hover:border-secondary/50 transition-colors">
                    <div className="absolute inset-0 bg-primary/40 group-hover:bg-transparent transition-colors z-10"></div>
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-64 md:h-full object-cover rounded-lg"
                      onError={(e) => {
                        e.currentTarget.src = 'https://via.placeholder.com/600x400/112240/ccd6f6?text=Project+Screenshot';
                      }}
                    />
                  </a>
                </div>
                <div className="md:w-1/2 flex flex-col justify-center">
                  <h3 className="text-2xl font-bold text-textPrimary mb-6">{project.title}</h3>
                  <div className="bg-[#112240] p-6 rounded-lg text-textSecondary text-base space-y-4 mb-6 shadow-xl relative z-20">
                    <p><strong className="text-secondary">{lang === 'en' ? 'The Problem:' : 'O Desafio:'}</strong> {project.problem}</p>
                    <p><strong className="text-secondary">{lang === 'en' ? 'The Solution:' : 'A Solução:'}</strong> {project.solution}</p>
                    <p><strong className="text-secondary">{lang === 'en' ? 'The Outcome:' : 'O Resultado:'}</strong> {project.outcome}</p>
                  </div>
                  <div className="flex flex-wrap gap-3 mb-6">
                    {project.tech.map((techItem) => (
                      <span key={techItem} className="text-sm font-mono text-textSecondary">
                        {techItem}
                      </span>
                    ))}
                  </div>
                  <a
                    href={project.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-secondary hover:underline group"
                  >
                    {lang === 'en' ? 'Visit Live Project' : 'Visitar Projeto Ao Vivo'}
                    <FiChevronRight className="ml-1 group-hover:translate-x-1 transition-transform" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </section>

      {/* Testimonials Placeholder */}
      <section id="testimonials" className="py-24 border-t border-textSecondary/20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true, margin: "-100px" }}
          className="text-center"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-textPrimary mb-4">
            {t.testimonials.title}
          </h2>
          <p className="text-textSecondary mb-12 max-w-2xl mx-auto">{t.testimonials.subtitle}</p>
          <div className="bg-[#112240] p-12 rounded-lg border border-dashed border-textSecondary/30 max-w-3xl mx-auto">
            <p className="text-textSecondary italic">{t.testimonials.placeholder}</p>
          </div>
        </motion.div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 border-t border-textSecondary/20 mb-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true, margin: "-100px" }}
          className="max-w-3xl mx-auto"
        >
          <h2 className="text-3xl md:text-5xl font-bold text-textPrimary mb-6 text-center">
            {t.contact.title}
          </h2>
          <p className="text-textSecondary text-center text-lg mb-12">
            {t.contact.subtitle}
          </p>

          <form 
            className="space-y-6" 
            name="contact" 
            method="POST" 
            data-netlify="true"
            onSubmit={(e) => {
              e.preventDefault();
              const form = e.target as HTMLFormElement;
              const formData = new FormData(form);
              fetch('/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams(formData as any).toString()
              })
              .then(() => {
                alert('Thank you! Your message has been sent.');
                form.reset();
              })
              .catch((error) => alert('There was an error sending the message: ' + error));
            }}
          >
            <input type="hidden" name="form-name" value="contact" />
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-textSecondary text-sm mb-2" htmlFor="name">{t.contact.form.name}</label>
                <input className="w-full bg-[#112240] border border-textSecondary/20 rounded p-3 text-textPrimary focus:outline-none focus:border-secondary transition-colors" type="text" id="name" name="name" required />
              </div>
              <div>
                <label className="block text-textSecondary text-sm mb-2" htmlFor="email">{t.contact.form.email}</label>
                <input className="w-full bg-[#112240] border border-textSecondary/20 rounded p-3 text-textPrimary focus:outline-none focus:border-secondary transition-colors" type="email" id="email" name="email" required />
              </div>
            </div>
            <div>
              <label className="block text-textSecondary text-sm mb-2" htmlFor="company">{t.contact.form.company}</label>
              <input className="w-full bg-[#112240] border border-textSecondary/20 rounded p-3 text-textPrimary focus:outline-none focus:border-secondary transition-colors" type="text" id="company" name="company" />
            </div>
            <div>
              <label className="block text-textSecondary text-sm mb-2" htmlFor="message">Message</label>
              <textarea className="w-full bg-[#112240] border border-textSecondary/20 rounded p-3 text-textPrimary focus:outline-none focus:border-secondary transition-colors h-32 resize-none" id="message" name="message" placeholder={t.contact.form.message} required></textarea>
            </div>
            <p className="text-textSecondary text-sm italic">{t.contact.nextSteps}</p>
            <button type="submit" className="w-full md:w-auto px-8 py-4 bg-transparent border-2 border-secondary text-secondary font-bold rounded hover:bg-secondary/10 transition-colors">
              {t.contact.form.submit}
            </button>
          </form>
        </motion.div>
      </section>

      {/* Language Toggle fixed at bottom right for easy access */}
      <div className="fixed bottom-6 right-6 z-50 flex space-x-2 bg-[#112240] p-2 rounded-full border border-textSecondary/20 shadow-xl">
        <button
          onClick={() => setLang('en')}
          className={`flex items-center justify-center w-10 h-10 rounded-full transition-all ${lang === 'en' ? 'bg-secondary text-primary font-bold' : 'text-textSecondary hover:text-textPrimary'}`}
          title="English"
        >
          EN
        </button>
        <button
          onClick={() => setLang('pt')}
          className={`flex items-center justify-center w-10 h-10 rounded-full transition-all ${lang === 'pt' ? 'bg-secondary text-primary font-bold' : 'text-textSecondary hover:text-textPrimary'}`}
          title="Português"
        >
          PT
        </button>
      </div>
    </>
  );
}

function App() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [lang, setLang] = useState<Language>('en');

  // Must ensure navbar matches the same auto-detected language
  useEffect(() => {
    const userLanguage = navigator.language || (navigator as any).userLanguage;
    if (userLanguage && userLanguage.toLowerCase().startsWith('pt')) {
      setLang('pt');
    }

    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', (e) => {
        e.preventDefault();
        const target = e.currentTarget as HTMLAnchorElement;
        const href = target.getAttribute('href');
        if (href) {
          document.querySelector(href)?.scrollIntoView({
            behavior: 'smooth'
          });
          setIsMenuOpen(false);
        }
      });
    });
  }, []);

  const t = text[lang];

  return (
    <LazyMotion features={domAnimation}>
      <div className="min-h-screen bg-primary font-sans">
        <nav className="fixed w-full bg-primary/90 backdrop-blur-md z-40 shadow-sm border-b border-textSecondary/10">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="text-secondary font-bold text-2xl tracking-tighter"
          >
            <Link to="/">JK.</Link>
          </motion.div>

          <div className="hidden md:flex gap-8 items-center">
            <a href="#about" className="text-textPrimary text-sm font-medium hover:text-secondary transition-colors">{t.nav.about}</a>
            <a href="#services" className="text-textPrimary text-sm font-medium hover:text-secondary transition-colors">{t.nav.services}</a>
            <a href="#projects" className="text-textPrimary text-sm font-medium hover:text-secondary transition-colors">{t.nav.projects}</a>
            <a href="#contact" className="px-5 py-2 border border-secondary text-secondary text-sm rounded hover:bg-secondary/10 transition-colors">{t.nav.contact}</a>
            <div className="flex space-x-4 border-l border-textSecondary/30 pl-6 ml-2">
              <a href="https://github.com/Jatan-Kumar" target="_blank" rel="noopener noreferrer" className="text-textPrimary hover:text-secondary transition-colors"><FiGithub size={20} /></a>
              <a href="https://www.linkedin.com/in/jatan-kumar-570a7916b" target="_blank" rel="noopener noreferrer" className="text-textPrimary hover:text-secondary transition-colors"><FiLinkedin size={20} /></a>
              <a href="mailto:jatan-c.k@hotmail.com" className="text-textPrimary hover:text-secondary transition-colors"><FiMail size={20} /></a>
            </div>
          </div>

          <motion.button
            className="md:hidden text-textPrimary hover:text-secondary transition-colors"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            whileTap={{ scale: 0.95 }}
          >
            <AnimatePresence mode="wait">
              {isMenuOpen ? (
                <motion.div key="close" initial={{ rotate: -180, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 180, opacity: 0 }} transition={{ duration: 0.2 }}>
                  <FiX size={28} />
                </motion.div>
              ) : (
                <motion.div key="menu" initial={{ rotate: 180, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -180, opacity: 0 }} transition={{ duration: 0.2 }}>
                  <FiMenu size={28} />
                </motion.div>
              )}
            </AnimatePresence>
          </motion.button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: '100vh' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="absolute top-full left-0 w-full bg-[#112240] md:hidden shadow-xl"
            >
              <div className="flex flex-col items-center justify-center p-8 gap-8">
                {['about', 'services', 'projects', 'contact'].map((item) => (
                  <a
                    key={item}
                    href={`#${item}`}
                    className="text-xl font-medium text-textPrimary hover:text-secondary transition-colors"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {t.nav[item as keyof typeof t.nav]}
                  </a>
                ))}
                <div className="flex space-x-6 mt-4">
                  <a href="https://github.com/Jatan-Kumar" target="_blank" rel="noopener noreferrer" className="text-textPrimary hover:text-secondary transition-colors"><FiGithub size={24} /></a>
                  <a href="https://www.linkedin.com/in/jatan-kumar-570a7916b" target="_blank" rel="noopener noreferrer" className="text-textPrimary hover:text-secondary transition-colors"><FiLinkedin size={24} /></a>
                  <a href="mailto:jatan-c.k@hotmail.com" className="text-textPrimary hover:text-secondary transition-colors"><FiMail size={24} /></a>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      <main className="container mx-auto px-6 max-w-6xl">
        <Home />
      </main>

      <footer className="bg-[#112240] py-8 text-center text-textSecondary text-sm border-t border-textSecondary/10 pb-24">
        <p>{t.footer}</p>
      </footer>
      </div>
    </LazyMotion>
  );
}

export default App;